import { Component, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { FormGroup, FormBuilder } from '@angular/forms';
import { ColumnDefinitionFactory } from '../../../core/service/factory/column-definition-factory.service';
import { FormControlExFactory } from '../../../core/service/factory/form-control-ex-factory.service';
import { ColumnDefinition, ColumnVisibility } from '../../../core/model/column-definition';
import { CompanyService } from '../../../rent/service/data/company.service';
import { LocalDate } from '../../../core/model/local-date';
import { ReportService } from '../../service/data/report.service';
import { NativeWindowService } from '../../../core/service/native-window.service';
import { SelectionItem } from '../../../core/component/select/select.component';
import { VatReport } from '../../model/vat-report';
import { Subscription } from 'rxjs';
import { NavigationService } from '../../../core/service/navigation.service';
import { canSubmitReportForm } from '../../utility/report-form-validation';

@Component({
    selector: 'vat-report',
    templateUrl: './vat-report.component.html',
    styleUrls: ['./vat-report.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class VatReportComponent implements OnInit, OnDestroy {
  constructor(
    private _cdf: ColumnDefinitionFactory,
    private _companyService: CompanyService,
    private _navigationService: NavigationService,
    private _nativeWindow: NativeWindowService,
    private _dataService: ReportService,
    fb: FormBuilder,
    fcf: FormControlExFactory
  ) {
    const fromDate = LocalDate.fromDate(LocalDate.fromDate(LocalDate.today().addMonths(-1)).firstDayInMonth());
    const toDate = LocalDate.fromDate(fromDate.lastDayInMonth());

    this.form = fb.group({
      company: fcf.createMandatory('COMMON.COMPANY', null),
      periodFrom: fcf.createMandatory('INVOICE.PERIOD_FROM', fromDate),
      periodTo: fcf.createMandatory('INVOICE.PERIOD_TO', toDate),
    });

    this.columnDefinitions = [
      this._cdf.create('INVOICE.INVOICE_NUMBER', 'invoiceNumber'),
      this._cdf.create('INVOICE.RECIPIENT_NAME', 'invoiceRecipient', ColumnVisibility.optional),
      this._cdf.create('INVOICE.PERIOD', 'periodText'),
      this._cdf.createMoney('VAT_REPORT.VAT_BASE_REVENUE', 'vatBasingRevenue'),
      this._cdf.createMoney('VAT_REPORT.VAT_REALIZED_BEFORE_PERIOD', 'vatRealizedBeforePeriod'),
      this._cdf.createMoney('VAT_REPORT.VAT_REALIZED_IN_PERIOD', 'vatRealizedInPeriod'),
      this._cdf.createMoney('VAT_REPORT.VAT_BASE_IN_PERIOD', 'vatBasingRevenueInPeriod'),
      this._cdf.createMoney('VAT_REPORT.VAT_BASE_AFTER_PERIOD', 'vatBasingRevenueAfterPeriod'),
      this._cdf.createMoney('ACCOUNT_PLAN.TURNOVER_CORRECTION_NOVAT_ACCOUNT', 'turnoverNoVat'),
      this._cdf.createMoney('ACCOUNT_PLAN.TURNOVER_CORRECTION_VAT25_ACCOUNT', 'turnover25'),
      this._cdf.createMoney('ACCOUNT_PLAN.TURNOVER_CORRECTION_VAT25_VOLUNTARY_ACCOUNT', 'turnover25Voluntary'),
      this._cdf.createMoney('ACCOUNT_PLAN.TURNOVER_CORRECTION_VAT12_ACCOUNT', 'turnover12', ColumnVisibility.optional),
      this._cdf.createMoney('ACCOUNT_PLAN.TURNOVER_CORRECTION_VAT12_VOLUNTARY_ACCOUNT', 'turnover12Voluntary', ColumnVisibility.optional),
      this._cdf.createMoney('ACCOUNT_PLAN.TURNOVER_CORRECTION_VAT6_ACCOUNT', 'turnover6', ColumnVisibility.optional),
      this._cdf.createMoney('ACCOUNT_PLAN.TURNOVER_CORRECTION_VAT6_VOLUNTARY_ACCOUNT', 'turnover6Voluntary', ColumnVisibility.optional),
    ];
  }

  columnDefinitions: ColumnDefinition[];
  companies: SelectionItem[];
  dataContext: VatReport;
  form: FormGroup;

  ngOnDestroy(): void {
    if (this._sub) {
      this._sub.unsubscribe();
    }
  }

  ngOnInit() {
    this._companyService.getList().subscribe(data => {
      this.companies = data.map(item => new SelectionItem(item.name, item.id));

      if (this.companies.length) {
        this.form.controls['company'].setValue(this.companies[0].value);
      }
    });
  }

  onPrintPages() {
    if (!canSubmitReportForm(this.form)) return;
    window.print();
  }

  exportExcel(): void {
    if (this._sub) this._sub.unsubscribe();

    this._sub = this._dataService
      .getVatReportExcel(this.form.controls['company'].value, this.form.controls['periodFrom'].value, this.form.controls['periodTo'].value)
      .subscribe(file => {
        this._nativeWindow.save(file);
      });
  }

  private _sub: Subscription;

  onRowClicked(e: { context: any; event: any }) {
    this._navigationService.navigate(['invoices', 'edit', e.context.invoiceId], null, e.event.ctrlKey);
  }

  onSubmit() {
    if (!canSubmitReportForm(this.form)) return;

    if (this._sub) {
      this._sub.unsubscribe();
    }

    this._sub = this._dataService
      .getVatReport(this.form.controls['company'].value, this.form.controls['periodFrom'].value, this.form.controls['periodTo'].value)
      .subscribe(data => {
        this.dataContext = data;
      });
  }
}
